import Constants from '@/Constants';
import { BotRecommendationRequest } from '@/models/BotRecommendationRequest';
import Category from '@/models/Category';
import { ApplicationState } from '@/store';
import {
    buildingContentRecommendations,
    buildingContentRecommendationsClear,
    getChatCategoriesClear,
    getChatCategoriesRequest,
    sendChatMessage,
} from '@/store/bot/actions';
import React, { useEffect, useRef, useState } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import _ from 'underscore';
import './style.scss';

interface UICategory extends Category {
    selected: boolean;
}

const ChatCategoriesMessage: React.FC = () => {
    const { organizationId, categories, strings } = useSelector((state: ApplicationState) => ({
        organizationId: state.identity.data.person.orgId,
        categories: state.chat.categoriesList,
        strings: state.locale.data,
    }));

    const [categoriesChunk, setCategoriesChunk] = useState<UICategory[][]>([]);
    const [selectedCategoriesIds, setSelectedCategoriesIds] = useState<number[]>([]);
    const [sendBtnDisabled, setSendBtnDisabled] = useState<boolean>(true);

    const dispatch = useDispatch();
    const categoriesContainerRef = useRef<HTMLDivElement>(null);
    const MAX_CATEGORIES_PER_PAGE: number = 7;

    const getCarouselDots = (dots: boolean) => {
        return <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={4} />;
    };

    const settings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        appendDots: (dots: boolean) => {
            return getCarouselDots(dots);
        },
    };

    const onPressCategoryHandler = (category: UICategory) => {
        if (category.id) {
            const tmpCategories: number[] = [...selectedCategoriesIds];
            const indexToRemove: number = tmpCategories.indexOf(category.id);
            category.selected = indexToRemove === -1;

            if (category.selected) {
                tmpCategories.push(category.id);
            } else {
                tmpCategories.splice(indexToRemove, 1);
            }

            setSelectedCategoriesIds(tmpCategories);
            setSendBtnDisabled(tmpCategories.length === 0);
        }
    };

    const getCategoryAsChips = (categoryList: UICategory[]): JSX.Element => (
        <div className="page" key={_.uniqueId('chips_page_')}>
            <div className="chips-container">
                {categoryList.map((category: UICategory) => {
                    return (
                        <button
                            className={`chip ${category.selected ? 'marked' : ''}`}
                            key={_.uniqueId('chips_')}
                            onClick={() => onPressCategoryHandler(category)}
                            type="button"
                        >
                            {category.name}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const onPressSendCategoryHandler = () => {
        dispatch(buildingContentRecommendations({ categoryIds: selectedCategoriesIds.join(',') } as BotRecommendationRequest));
        dispatch(sendChatMessage({ message: strings.BOT_CATEGORIES_SELECTED, isAnswer: true }));
    };

    useEffect(() => {
        if (categories) {
            const categoriesHasContent: Category[] = categories.filter(
                (category: Category) => category.contentsAmount && category.contentsAmount > 0,
            );
            setCategoriesChunk(_.chunk(categoriesHasContent, MAX_CATEGORIES_PER_PAGE) as UICategory[][]);
        }
    }, [categories]);

    useEffect(() => {
        if (categoriesChunk.length > 0) {
            categoriesContainerRef.current?.scrollIntoView();
        }
    }, [categoriesChunk]);

    useEffect(() => {
        dispatch(buildingContentRecommendationsClear());
        dispatch(
            getChatCategoriesRequest({
                applicationId: Constants.APP_ID,
                organizationId,
                withCoursesAmount: true,
                moduleIds: Constants.PLAYLIST_MODULE_ID.toString(),
            }),
        );
        return () => {
            dispatch(getChatCategoriesClear());
        };
    }, []);

    if (categoriesChunk.length === 0) {
        return null;
    }

    return (
        <div className="chat-categories-message" ref={categoriesContainerRef}>
            <div className="carrosell-slide-container">
                <Slider {...settings}>{categoriesChunk.map((item) => getCategoryAsChips(item))}</Slider>
            </div>
            <div className="btn-container">
                <button className={'btn-send'} disabled={sendBtnDisabled} onClick={onPressSendCategoryHandler} type="button">
                    {strings.BOT_CHIPS_SEND}
                </button>
            </div>
        </div>
    );
};

export default ChatCategoriesMessage;
